import { DisplayDay, MonthlyCalendarDays } from 'types/Calendar';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(weekday);

export const getDatesInMonthDisplay = (
  month: number,
  year: number,
  includeOverflownDays: boolean = true,
): DisplayDay[] => {
  const daysInMonth = getDaysInMonth(month, year);
  const firstWeekday = getFirstWeekdayOfMonth(month, year);
  const result: DisplayDay[] = [];

  const prev = getPrevMonthYear(month, year);
  const prevDaysInMonth = getDaysInMonth(prev.month, prev.year);

  if (includeOverflownDays) {
    // Add prev overflow dates...
    for (let j = firstWeekday - 1; j >= 0; j--) {
      const date = dayjs(
        `${prev.year}-${prev.month}-${prevDaysInMonth - j}`,
        'YYYY-MM-DD',
      );
      result.push({
        date,
        dateString: dayjs(date).format('YYYY-MM-DD'),
        isCurrentMonth: false,
        isWeekend: date.day() === 0 || date.day() === 6,
      });
    }
  }

  // Add current month's dates
  for (let i = 1; i <= daysInMonth; i++) {
    const date = dayjs(`${year}-${month}-${i}`, 'YYYY-MM-DD');

    result.push({
      date,
      dateString: dayjs(date).format('YYYY-MM-DD'),
      isCurrentMonth: true,
      isWeekend: date.day() === 0 || date.day() === 6,
    });
  }

  if (includeOverflownDays) {
    // Overflow dates for next month to meet 42 days per month display   requirement
    const MAX_POSSIBLE_DAYS = 42;

    if (result.length < MAX_POSSIBLE_DAYS) {
      const daysToAdd = MAX_POSSIBLE_DAYS - result.length;

      const next = getNextMonthYear(month, year);

      for (let k = 1; k <= daysToAdd; k++) {
        const noOverflowDaysNeeded = result.length % 7 === 0;

        if (noOverflowDaysNeeded) {
          return result;
        }

        const date = dayjs(`${next.year}-${next.month}-${k}`, 'YYYY-MM-DD');

        result.push({
          date,
          dateString: dayjs(date).format('YYYY-MM-DD'),
          isCurrentMonth: false,
          isWeekend: date.day() === 0 || date.day() === 6,
        });
      }
    }
  }

  return result;
};

const getDaysInMonth = (month: number, year: number) => {
  return dayjs(`${year}-${month}`, `YYYY-MM`).daysInMonth();
};

export const getFirstWeekdayOfMonth = (month: number, year: number) => {
  return dayjs(`${year}-${month}`, `YYYY-MM`).startOf('month').weekday();
};

const getPrevMonthYear = (month: number, year: number) => {
  // If it is January... prev month is Dec of the previous year
  if (month === 1) {
    return {
      month: 12,
      year: year - 1,
    };
  }

  // Otherwise, same year, but month - 1
  return {
    month: month - 1,
    year,
  };
};

const getNextMonthYear = (month: number, year: number) => {
  // If it is December... next month it's January of next year
  if (month === 12) {
    return {
      month: 1,
      year: year + 1,
    };
  }

  // Otherwise, same year, but month + 1
  return {
    month: month + 1,
    year: year,
  };
};

const CalendarUtils = {
  getMonthsBetween(startDate: string, endDate: string): dayjs.Dayjs[] {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const months: dayjs.Dayjs[] = [];

    let currentDate = start;
    while (currentDate.isSame(end, 'month') || currentDate.isBefore(end)) {
      months.push(currentDate);
      currentDate = currentDate.add(1, 'month');
    }

    return months;
  },
  getMonthDays(date: string) {
    return getDatesInMonthDisplay(dayjs(date).month() + 1, dayjs(date).year());
  },
  getWeekRange(date: string): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
    const current = dayjs(date);

    return {
      start: current.weekday(0),
      end: current.weekday(6),
    };
  },
  resolveWeekAnchor(date: string): string {
    const current = dayjs(date);
    const anchor = current.isSame(dayjs(), 'month')
      ? dayjs()
      : current.startOf('month');

    return anchor.format('YYYY-MM-DD');
  },
  getWeekDays(date: string): DisplayDay[] {
    const start = dayjs(date).weekday(0);

    return Array.from({ length: 7 }, (_, index) => {
      const day = start.add(index, 'day');

      return {
        date: day,
        dateString: day.format('YYYY-MM-DD'),
        isCurrentMonth: true,
        isWeekend: day.day() === 0 || day.day() === 6,
      };
    });
  },
  getMonthlyDays(
    startDate: string,
    endDate: string,
    includeOverflownDays?: boolean,
  ) {
    const monthlyCalendarDates: MonthlyCalendarDays = {};

    const months = CalendarUtils.getMonthsBetween(startDate, endDate);

    months.forEach((month) => {
      const monthKey = month.format('YYYY-MM');

      monthlyCalendarDates[monthKey] = {
        month: month,
        days: getDatesInMonthDisplay(
          month.month() + 1,
          month.year(),
          includeOverflownDays,
        ),
      };
    });

    return Object.entries(monthlyCalendarDates);
  },
};

export default CalendarUtils;
